import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiBriefcase, FiMail, FiLock, FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi';
import { useAuthStore } from '../store/authStore';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [formData, setFormData] = useState({
    companyName: '',
    companyType: 'corriere',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validazioni
    if (!formData.companyName.trim()) {
      setError('Nome azienda obbligatorio');
      return;
    }

    if (formData.password.length < 6) {
      setError('La password deve avere almeno 6 caratteri');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/register`,
        {
          email: formData.email,
          password: formData.password,
          companyName: formData.companyName,
          companyType: formData.companyType
        }
      );

      const { token, user } = response.data;
      login(user, token);
      navigate('/dashboard-cliente');
    } catch (err) {
      setError(err.response?.data?.error || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Compila il form', description: 'Inserisci i dati della tua azienda' },
    { number: 2, title: 'Conferma email', description: 'Riceverai una conferma via email' },
    { number: 3, title: 'Inizia a prenotare', description: 'Accedi e gestisci le prenotazioni' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Navigation Bar */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-semibold transition"
          >
            <FiArrowLeft /> Indietro
          </button>
          <div className="flex items-center gap-3">
            <div className="text-3xl">♻️</div>
            <div>
              <h3 className="font-bold text-gray-800">EcoPrenota</h3>
              <p className="text-xs text-gray-500">Registrazione</p>
            </div>
          </div>
          <div></div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Column - Steps */}
          <div className="order-2 md:order-1">
            {/* Hero Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Unisciti a EcoPrenota
              </h2>
              <p className="text-gray-600 mb-8">
                Registra la tua azienda e inizia a gestire le prenotazioni in modo sostenibile e efficiente.
              </p>

              {/* Steps */}
              <div className="space-y-6">
                {steps.map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-green-100 text-green-600 font-bold text-lg">
                        {step.number}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 mb-1">{step.title}</h3>
                      <p className="text-gray-600 text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits Card */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FiCheckCircle className="text-green-600" /> Vantaggi
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Gestione prenotazioni centralizzata</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Visualizzazione slot disponibili in tempo reale</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Storico completo delle operazioni</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span>Supporto 24/7</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Registration Form */}
          <div className="order-1 md:order-2">
            <div className="bg-white rounded-3xl shadow-2xl p-10 border border-gray-100">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  Crea account
                </h1>
                <p className="text-gray-600">
                  Compila il modulo per registrare la tua azienda
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-5 py-4 rounded-xl mb-6 flex items-start gap-3">
                  <span className="text-xl mt-1">⚠️</span>
                  <div>
                    <p className="font-semibold text-sm">Errore</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Nome Azienda */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">
                    🏢 Nome Azienda
                  </label>
                  <div className="relative">
                    <FiBriefcase className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Es. Corriere Express SpA"
                      required
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition bg-gray-50 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Tipo Azienda */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">
                    🏭 Tipo Azienda
                  </label>
                  <select
                    name="companyType"
                    value={formData.companyType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition bg-gray-50 focus:bg-white font-medium"
                  >
                    <option value="corriere">📦 Corriere (trasporto)</option>
                    <option value="cartiera">📄 Cartiera (produzione)</option>
                  </select>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">
                    📧 Email
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="info@azienda.com"
                      required
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition bg-gray-50 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">
                    🔐 Password
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Minimo 6 caratteri"
                      required
                      className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition bg-gray-50 focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                {/* Conferma Password */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3 text-sm">
                    🔐 Conferma Password
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-4 text-gray-400" size={20} />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Ripeti la password"
                      required
                      className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition bg-gray-50 focus:bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-4 rounded-xl transition transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl mt-8"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span> Registrazione in corso...
                    </span>
                  ) : (
                    '✓ Registrati'
                  )}
                </button>
              </form>

              {/* Login Link */}
              <div className="text-center mt-8 pt-6 border-t border-gray-200">
                <p className="text-gray-600 text-sm">
                  Hai già un account?{' '}
                  <button
                    onClick={() => navigate('/')}
                    className="text-green-600 font-bold hover:text-green-700 underline transition"
                  >
                    Accedi qui
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 pt-8 mt-20">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              © 2026 EcoPrenota • Sistema di gestione sostenibile per impianti di riciclo
            </p>
            <p className="text-gray-400 text-xs mt-3">
              I tuoi dati sono protetti e gestiti in conformità alle normative sulla privacy
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default RegisterPage;
